// Copyright (c) 2026. CYBERKOALA.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.
import { REMOTE_PLAY_ENDPOINT } from '@/config';

export type Pad = { i: number; b: number[]; a: number[] }; // pad index, buttons (standard mapping), axes
export type Stats = {
  rtt?: number;
  path?: string;
  fps?: number;
  kbps?: number;
  res?: string;
  codec?: string;
  jitterBufferMs?: number;
  decodeMs?: number;
  encodeMs?: number;
  limit?: string;
  lost?: number;
};
export type Status = 'connecting' | 'connected' | 'reconnecting' | 'host-left';
export type DegradationPreference = 'balanced' | 'maintain-resolution' | 'maintain-framerate';
export type VideoCodec = 'H264' | 'VP8' | 'VP9' | 'AV1';

const CODEC_PRIORITY: VideoCodec[] = ['H264', 'VP8', 'VP9', 'AV1'];
const SIGNAL: string = REMOTE_PLAY_ENDPOINT;

/** Never trust remote input: clamp sizes and ranges before it reaches the OS gamepad driver. */
export function sanitizePad(x: unknown): Pad | null {
  const o = x as any;
  if (!o || typeof o !== 'object' || !Number.isInteger(o.i) || o.i < 0 || o.i > 15) return null;
  const arr = (v: unknown, max: number, lo: number, hi: number) =>
    Array.isArray(v)
      ? v
          .slice(0, max)
          .map((n) => (typeof n === 'number' && isFinite(n) ? Math.min(hi, Math.max(lo, n)) : 0))
      : [];
  return { i: o.i, b: arr(o.b, 20, 0, 1), a: arr(o.a, 8, -1, 1) };
}

type Handlers = {
  onStream?: (s: MediaStream | null) => void;
  onLocalStream?: (s: MediaStream | null) => void; // локальный захват хоста
  onPad?: (peer: string, p: Pad) => void;
  onLeave?: (peer: string) => void;
  onStatus?: (s: Status) => void;
  onFatal?: (reason: string) => void;
  onEnded?: () => void;
};

function preferVideoCodecs(pc: RTCPeerConnection, preferredCodec: VideoCodec = 'H264') {
  try {
    const caps = RTCRtpSender.getCapabilities('video');
    if (!caps?.codecs) return;

    const supported = caps.codecs;

    const primary = supported.filter(
      (c) =>
        !['video/rtx', 'video/red', 'video/ulpfec', 'video/flexfec-03'].includes(
          c.mimeType.toLowerCase()
        )
    );

    const auxiliary = supported.filter((c) => !primary.includes(c));

    const orderedPrimary = [
      ...primary.filter((c) => c.mimeType.toUpperCase() === `VIDEO/${preferredCodec}`),
      ...CODEC_PRIORITY.filter((codec) => codec !== preferredCodec).flatMap((codec) =>
        primary.filter((c) => c.mimeType.toUpperCase() === `VIDEO/${codec}`)
      ),
      ...primary.filter(
        (c) => !CODEC_PRIORITY.some((codec) => c.mimeType.toUpperCase() === `VIDEO/${codec}`)
      ),
    ];

    const ordered = [...orderedPrimary, ...auxiliary];

    pc.getTransceivers().forEach((t) => {
      if (t.sender.track?.kind === 'video' && ordered.length) {
        t.setCodecPreferences(ordered);
      }
    });

  } catch (e) {
    console.warn('[Play Together] Codec preferences unavailable:', e);
  }
}

async function capBitrate(
  pc: RTCPeerConnection,
  maxBitrate: number,
  degradationPreference: DegradationPreference
) {
  for (const sender of pc.getSenders()) {
    if (sender.track?.kind !== 'video') continue;

    try {
      const params = sender.getParameters();

      if (!params.encodings?.length) {
        params.encodings = [{}];
      }

      const encoding = params.encodings[0];

      encoding.maxBitrate = maxBitrate;
      encoding.maxFramerate = 60;

      Object.assign(encoding, {
        priority: 'high',
        networkPriority: 'high',
      });

      params.degradationPreference = degradationPreference;

      await sender.setParameters(params);
    } catch (e) {
      console.warn('[Play Together] Failed to configure encoder:', e);
    }
  }
}

export class Session {
  private ws?: WebSocket;
  private ice: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
  private pcs = new Map<string, RTCPeerConnection>();
  private chans = new Map<string, RTCDataChannel>();
  private stream?: MediaStream;
  private q: Promise<void> = Promise.resolve();
  private stopped = false;
  private attempt = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private hostId: string | null = null;
  private last: Record<string, number> = {};
  private videoOn: boolean;
  private peerVideo = new Map<string, boolean>(); // host: does each guest want video?
  private peerDegradation = new Map<string, DegradationPreference>();
  private pendingOffer = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private room: string,
    private isHost: boolean,
    private h: Handlers,
    video = true,
    private quality: '720' | '1080' = '1080',
    private degradationPreference: DegradationPreference = 'balanced',
  private codec: VideoCodec = 'H264'
  ) {
    this.videoOn = video;
  }

  async start() {
    if (this.isHost && this.videoOn) await this.capture();
    this.connect();
  }

  private async capture() {
    const [w, h] = this.quality === '720' ? [1280, 720] : [1920, 1080]; // never capture 1440p/4K: it explodes encode time and latency
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: { ideal: w, max: w },
        height: { ideal: h, max: h },
        frameRate: { ideal: 60, max: 60 },
      },
      audio: true,
    });
    stream.getVideoTracks().forEach((t) => {
      t.contentHint = 'motion';
      t.onended = () => {
        if (this.stream === stream) {
          void this.setVideo(false);
          this.h.onEnded?.();
        }
      };
    });
    this.stream = stream;
    this.h.onLocalStream?.(stream);
  }

  private releaseStream() {
    const s = this.stream;
    this.stream = undefined;
    this.h.onLocalStream?.(null);

    s?.getTracks().forEach((t) => {
      t.onended = null;
      t.stop();
    });
  }

  /** Host: start/stop screen capture. Guest: start/stop receiving video. Gamepad input keeps working either way. */
  async setVideo(on: boolean) {
    if (on === this.videoOn) return;
    if (this.isHost) {
      if (on)
        await this.capture(); // throws if the user cancels the picker; nothing changes then
      else this.releaseStream();
      this.videoOn = on;
      [...this.pcs.keys()].forEach((p) => void this.offer(p)); // fresh offers add or remove the media tracks
    } else {
      this.videoOn = on;
      this.sendHello(); // tell the host; it re-offers without (or with) video for this guest only
    }
  }

  /** guest -> host over the unreliable, unordered data channel */
  sendPad(p: Pad) {
    const s = JSON.stringify(p);
    this.chans.forEach((c) => {
      if (c.readyState === 'open') c.send(s);
    });
  }

  private d(k: string, v: number) {
    const p = this.last[k];
    this.last[k] = v;
    return p === undefined ? undefined : v - p;
  }

  /** Live latency diagnostics for the first connected peer (call about once per second). */
  async getStats(): Promise<Stats> {
    const out: Stats = {};
    const pc = [...this.pcs.values()][0];
    if (!pc) return out;
    const rep = await pc.getStats();
    rep.forEach((r: any) => {
      if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') {
        out.rtt = Math.round((r.currentRoundTripTime ?? 0) * 1000);
        const l = rep.get(r.localCandidateId) as any,
          rm = rep.get(r.remoteCandidateId) as any;
        out.path = `${l?.candidateType}/${rm?.candidateType}${l?.relayProtocol ? ' via ' + l.relayProtocol : ''}`;
      } else if (r.type === 'inbound-rtp' && r.kind === 'video') {
        const codec = r.codecId ? (rep.get(r.codecId) as any) : undefined;
        if (codec?.mimeType) {
          const profile = codec.sdpFmtpLine?.match(/profile-level-id=([a-f0-9]+)/i)?.[1];
          out.codec = codec.mimeType.replace('video/', '') + (profile ? ` (${profile})` : '');
        }
        const dd = this.d('jbd', r.jitterBufferDelay),
          dc = this.d('jbc', r.jitterBufferEmittedCount);
        if (dd !== undefined && dc) out.jitterBufferMs = Math.round((1000 * dd) / dc);
        const dt = this.d('dec', r.totalDecodeTime),
          df = this.d('decf', r.framesDecoded);
        if (dt !== undefined && df) out.decodeMs = +((1000 * dt) / df).toFixed(1);
        const db = this.d('bin', r.bytesReceived),
          ts = this.d('tin', r.timestamp);
        if (db !== undefined && ts) out.kbps = Math.round((db * 8) / ts);
        out.fps = r.framesPerSecond;
        out.lost = r.packetsLost;
        out.res = `${r.frameWidth}x${r.frameHeight}`;
      } else if (r.type === 'outbound-rtp' && r.kind === 'video') {
        const codec = r.codecId ? (rep.get(r.codecId) as any) : undefined;
        if (codec?.mimeType) {
          const profile = codec.sdpFmtpLine?.match(/profile-level-id=([a-f0-9]+)/i)?.[1];
          out.codec = codec.mimeType.replace('video/', '') + (profile ? ` (${profile})` : '');
        }
        const et = this.d('enc', r.totalEncodeTime),
          ef = this.d('encf', r.framesEncoded);
        if (et !== undefined && ef) out.encodeMs = +((1000 * et) / ef).toFixed(1);
        const db = this.d('bout', r.bytesSent),
          ts = this.d('tout', r.timestamp);
        if (db !== undefined && ts) out.kbps = Math.round((db * 8) / ts);
        out.fps = r.framesPerSecond;
        out.limit = r.qualityLimitationReason;
        out.res = `${r.frameWidth}x${r.frameHeight}`;
      }
    });
    return out;
  }

  stop() {
    this.stopped = true;
    clearTimeout(this.timer);
    this.ws?.close();
    this.dropAll();
    this.pendingOffer.forEach((t) => clearTimeout(t));
    this.pendingOffer.clear();
    this.releaseStream();
  }

  private connect() {
    this.h.onStatus?.(this.attempt ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(SIGNAL);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'join', room: this.room, host: this.isHost }));
    ws.onmessage = (e) => {
      let m: any;
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      switch (m.type) {
        case 'welcome':
          this.attempt = 0;
          this.ice = m.ice ?? this.ice;
          this.hostId = m.hostId ?? null;
          this.h.onStatus?.('connected');
          if (this.isHost) (m.peers as string[]).forEach((p) => this.offerWhenReady(p));
          else this.sendHello();
          break;
        case 'peer-joined':
          if (m.host) {
            this.hostId = m.id;
            this.h.onStatus?.('connected');
            this.sendHello();
          } else if (this.isHost) this.offerWhenReady(m.id);
          break;
        case 'peer-left':
          this.drop(m.id);
          break;
        case 'signal':
          this.q = this.q.then(() => this.onSignal(m.from, m.data)).catch(() => {});
          break;
      }
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws || this.stopped) return;
      this.dropAll();
      if (ev.code >= 4000 && ev.code < 5000) {
        this.stopped = true;
        this.h.onFatal?.(ev.reason || String(ev.code));
        return;
      }
      this.h.onStatus?.('reconnecting');
      this.timer = setTimeout(() => this.connect(), Math.min(10000, 500 * 2 ** this.attempt++));
    };
  }

  private signal(to: string, data: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN)
      this.ws.send(JSON.stringify({ type: 'signal', to, data }));
  }

  /** guest -> host: "send me video or not". Sent before the host's first offer so a gamepad-only guest never receives media. */
  private sendHello() {
    if (!this.isHost && this.hostId) {
      this.signal(this.hostId, {
        hello: {
          video: this.videoOn,
          degradationPreference: this.degradationPreference,
        },
      });
    }
  }

  private offerWhenReady(peer: string) {
    // wait briefly for the guest's preference; older guests never send one
    if (this.peerVideo.has(peer)) return void this.offer(peer);
    clearTimeout(this.pendingOffer.get(peer));
    this.pendingOffer.set(
      peer,
      setTimeout(() => {
        this.pendingOffer.delete(peer);
        void this.offer(peer);
      }, 1500)
    );
  }

  private mk(peer: string) {
    const pc = new RTCPeerConnection({ iceServers: this.ice });
    this.pcs.set(peer, pc);
    pc.onicecandidate = (e) => {
      if (e.candidate) this.signal(peer, { ice: e.candidate });
    };
    pc.ontrack = (e) => {
      try {
        const receiver = e.receiver as any;
        receiver.jitterBufferTarget = 0;
        if ('playoutDelayHint' in receiver) {
          receiver.playoutDelayHint = 0;
        }
      } catch {
        // Browser does not support these optimizations.
      }

      this.h.onStream?.(e.streams[0]);
    };
    pc.onconnectionstatechange = () => {
      if (this.pcs.get(peer) === pc && pc.connectionState === 'failed' && this.isHost)
        void this.offer(peer); // retry fresh
    };
    return pc;
  }

  private async offer(peer: string) {
    try {
      this.pcs.get(peer)?.close();
      const pc = this.mk(peer);
      const sendVideo = this.videoOn && !!this.stream && this.peerVideo.get(peer) !== false;
      if (sendVideo) {
        // gamepad-only: no tracks at all, so no video or audio is ever encoded or sent
        for (const t of this.stream!.getTracks()) pc.addTrack(t, this.stream!);
        preferVideoCodecs(pc, this.codec);
      }
      const ch = pc.createDataChannel('input', { ordered: false, maxRetransmits: 0 });
      ch.onmessage = (e) => {
        let p: Pad | null = null;
        try {
          p = sanitizePad(JSON.parse(e.data));
        } catch {
          return;
        }
        if (p) this.h.onPad?.(peer, p);
      };
      await pc.setLocalDescription(await pc.createOffer());
      const peerPreference = this.peerDegradation.get(peer) ?? this.degradationPreference;
      await capBitrate(pc, this.quality === '720' ? 4_000_000 : 8_000_000, peerPreference);
      this.signal(peer, { sdp: pc.localDescription });
    } catch (err) {
      console.error('offer failed', err);
    }
  }

  private async onSignal(from: string, d: any) {
    if (d?.hello && this.isHost) {
      // A guest can request its preferred sender adaptation mode for this peer.
      const want = d.hello.video !== false;
      const requestedPreference = d.hello.degradationPreference;
      const validPreference: DegradationPreference =
        requestedPreference === 'maintain-resolution' ||
        requestedPreference === 'maintain-framerate' ||
        requestedPreference === 'balanced'
          ? requestedPreference
          : this.degradationPreference;
      const changed =
        (this.peerVideo.get(from) ?? true) !== want ||
        (this.peerDegradation.get(from) ?? this.degradationPreference) !== validPreference;
      this.peerVideo.set(from, want);
      this.peerDegradation.set(from, validPreference);
      const t = this.pendingOffer.get(from);
      if (t !== undefined) {
        clearTimeout(t);
        this.pendingOffer.delete(from);
        void this.offer(from);
      } else if (changed && this.pcs.has(from)) void this.offer(from);
      return;
    }
    let pc = this.pcs.get(from);
    if (d?.sdp) {
      if (d.sdp.type === 'offer') {
        // guests only ever receive fresh offers from the host
        pc?.close();
        pc = this.mk(from);
        pc.ondatachannel = (e) => this.chans.set(from, e.channel);
        if (!/^m=video/m.test(d.sdp.sdp ?? '')) this.h.onStream?.(null); // gamepad-only offer: nothing to show
      }
      if (!pc) return;
      await pc.setRemoteDescription(d.sdp);
      if (d.sdp.type === 'offer') {
        await pc.setLocalDescription(await pc.createAnswer());
        this.signal(from, { sdp: pc.localDescription });
      }
    } else if (d?.ice && pc) await pc.addIceCandidate(d.ice).catch(() => {});
  }

  private drop(peer: string) {
    this.pcs.get(peer)?.close();
    this.pcs.delete(peer);
    this.chans.delete(peer);
    this.peerVideo.delete(peer);
    this.peerDegradation.delete(peer);
    clearTimeout(this.pendingOffer.get(peer));
    this.pendingOffer.delete(peer);
    this.h.onLeave?.(peer);
    if (!this.isHost && peer === this.hostId) this.h.onStatus?.('host-left');
  }

  private dropAll() {
    [...this.pcs.keys()].forEach((p) => this.drop(p));
  }
}
